import type { AppMode } from '@iheartjane/types';
import { isEmbeddedMode } from '@iheartjane/types';
import { useContextEnsuringProvider } from '@iheartjane/utils';
import type { PropsWithChildren } from 'react';
import { createContext, useContext, useMemo } from 'react';

import { useHeadlessApp } from '../headlessAppProvider/headlessAppProvider';

interface EcommAppContextValues {
  appMode: AppMode;
  isEmbed: boolean;
  isIframe: boolean;
}

const ecommAppContextValue = (appMode: AppMode): EcommAppContextValues => {
  const isIframe = ['embedded', 'operatorEmbed'].includes(appMode);
  const isEmbed = isEmbeddedMode(appMode);
  return { appMode, isEmbed, isIframe };
};

const EcommAppContext = createContext<EcommAppContextValues | null>(null);
EcommAppContext.displayName = 'EcommApp';

/**
 * For components shared with non-Ecomm apps, which render outside of an
 * `EcommAppProvider`. Falls back to the default app mode instead of throwing.
 */
export const useSafeEcommApp = () =>
  useContext(EcommAppContext) ?? ecommAppContextValue('default');

export const useEcommApp = () => useContextEnsuringProvider(EcommAppContext);

export const EcommAppProvider = ({
  appMode,
  children,
}: PropsWithChildren<{ appMode: AppMode }>) => {
  const { options } = useHeadlessApp();
  const derivedAppMode = options?.bloomUserExperience ? 'bloom' : appMode;

  const contextValue = useMemo(() => {
    return ecommAppContextValue(derivedAppMode);
  }, [derivedAppMode]);

  return (
    <EcommAppContext.Provider value={contextValue}>
      {children}
    </EcommAppContext.Provider>
  );
};
